import React, { useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { FREE_TRIAL_DAYS, ProPlanId } from '../../constants/premium';
import {
  formatBillingFrequency,
  formatOfferDuration,
  getCouponDescription,
} from '../../helpers/premium';
import { ICoupon } from '../../interfaces/stripe';
import { Button } from '../Clickables/Buttons/Button';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { ClickableVariant } from '../Clickables/variants';
import { Heading } from '../Heading/Heading';
import { CheckboxIcon } from '../Icons/CheckboxIcon';
import { Text } from '../Text/Text';

import { CancellationText } from './components/CancellationText';
import { getPaymentPlans } from './helpers';

interface IProps {
  className?: string;
  coupon?: ICoupon;
  couponIsLoading?: boolean;
  backgroundColour?: string;
  proPlanId?: ProPlanId;
  onPlanSelect: (proPlanId: ProPlanId) => void;
  submitLink?: string;
  cancelLink?: string;
  cancelLinkVariant?: ClickableVariant;
  userHasBeenPro?: boolean;
  onSubmitLinkClick?: () => void;
  onCancelLinkClick?: () => void;
  planCardTitle?: string;
}

export const ProPaymentPlans = ({
  className,
  coupon,
  couponIsLoading = false,
  backgroundColour,
  proPlanId,
  onPlanSelect,
  submitLink,
  cancelLink,
  cancelLinkVariant = 'buttonSecondaryVariant',
  userHasBeenPro = false,
  onSubmitLinkClick,
  onCancelLinkClick,
  planCardTitle,
}: IProps) => {
  const paymentPlans = getPaymentPlans(coupon);
  const [hasSelectedPlan, setHasSelectedPlan] = useState<boolean>(false);

  useEffect(() => {
    const couponPlan = paymentPlans.some(plan => plan.planID === proPlanId);
    setHasSelectedPlan(couponPlan);
  }, [paymentPlans, proPlanId]);

  const getButtonText = (): string => {
    if (coupon) {
      return 'Claim your offer';
    }
    if (userHasBeenPro) {
      return 'Activate Pro';
    }
    return `Start ${FREE_TRIAL_DAYS} day free trial`;
  };

  return (
    <div
      className={`w-full rounded-lg ${backgroundColour} md:size-max`}
      data-testid="ProPaymentPlans"
    >
      <div className={twMerge('mx-auto flex w-full max-w-md flex-col items-center', className)}>
        {coupon ? (
          <div className="w-full pt-2 text-center md:pt-4">
            <Heading variant="2xs" className="my-2">
              {coupon.group.name}
            </Heading>
          </div>
        ) : (
          <Heading variant="2xs" className="mt-6 text-center">
            {planCardTitle || 'Select your plan'}
          </Heading>
        )}

        <div
          className={`${
            coupon ? 'mx-24' : ''
          } mt-5 flex w-full justify-center gap-x-2.5 sm:gap-x-4 md:mt-6`}
        >
          {!couponIsLoading &&
            paymentPlans.map(
              ({
                planID,
                promoDealText,
                label,
                price,
                pricePerMonth,
                summaryText,
                originalPrice,
                disabled,
              }) => {
                const selectedPlan = planID === proPlanId;
                return (
                  <div
                    className={`relative cursor-pointer ${
                      coupon ? 'w-4/5 sm:w-3/5' : 'w-1/2 sm:h-32 sm:w-52'
                    }`}
                    key={planID}
                    onClick={
                      disabled
                        ? undefined
                        : () => {
                            onPlanSelect(planID);
                          }
                    }
                    onKeyDown={
                      disabled
                        ? undefined
                        : () => {
                            onPlanSelect(planID);
                          }
                    }
                  >
                    <div
                      className={`col-start-1 row-start-1 size-full rounded-lg border-[3px] ${
                        selectedPlan ? 'border-pike' : 'border-twilight'
                      }`}
                    >
                      <div
                        className={`border-sea-foam flex size-full flex-col items-center justify-center rounded-[5px] border-[3px] ${
                          selectedPlan ? 'bg-pike-tint' : 'bg-sea-foam'
                        } py-4`}
                      >
                        <Text variant="sm" className={`mb-2 ${disabled ? 'text-disabled' : ''}`}>
                          {coupon ? formatOfferDuration(coupon) : label}
                        </Text>
                        <div>
                          {originalPrice && (
                            <>
                              <Heading variant="sm" as="span" className="line-through">
                                {originalPrice}
                              </Heading>{' '}
                            </>
                          )}
                          <Heading
                            variant="sm"
                            as="span"
                            className={`${disabled ? 'text-disabled' : ''} ${
                              originalPrice ? 'text-pinktail' : ''
                            }`}
                          >
                            {coupon ? price : pricePerMonth}
                          </Heading>

                          <Text
                            variant="sm"
                            as="span"
                            className={`font-semibold ${disabled ? 'text-disabled' : ''}`}
                          >
                            {coupon ? formatBillingFrequency(coupon) : '/mo'}
                          </Text>
                        </div>

                        {summaryText && (
                          <Text
                            variant="xs"
                            className={`m-0 text-center ${disabled ? 'text-disabled' : ''}`}
                          >
                            {summaryText}
                          </Text>
                        )}
                      </div>
                    </div>
                    {promoDealText && !disabled && (
                      <div className="absolute -top-3 col-start-1 row-start-1 flex w-full justify-center">
                        <div className="bg-deep-sea max-h-fit max-w-fit rounded-xl">
                          <Text variant="sm" className="text-sea-foam mb-0 px-3 py-1 font-semibold">
                            {promoDealText}
                          </Text>
                        </div>
                      </div>
                    )}
                    {selectedPlan && (
                      <div className="text-pike absolute -bottom-3 col-start-1 row-start-1 flex w-full justify-center">
                        <CheckboxIcon isGreen />
                      </div>
                    )}
                  </div>
                );
              },
            )}
        </div>
        {submitLink ? (
          <InternalLink
            disabled={!hasSelectedPlan}
            className="mt-6 w-full"
            variant="buttonPro"
            to={submitLink}
            onClick={onSubmitLinkClick}
          >
            {getButtonText()}
          </InternalLink>
        ) : (
          <Button
            disabled={!hasSelectedPlan}
            className="mt-6 w-full"
            variant="buttonPro"
            onClick={onSubmitLinkClick}
          >
            {getButtonText()}
          </Button>
        )}

        {cancelLink && (
          <InternalLink
            className="mt-4 w-full"
            variant={cancelLinkVariant}
            to={cancelLink}
            onClick={onCancelLinkClick}
          >
            No thanks
          </InternalLink>
        )}

        {coupon && (
          <Text variant="sm" className="mt-3 mb-6 w-80 text-center">
            {getCouponDescription(coupon)}
          </Text>
        )}
        <CancellationText />
      </div>
    </div>
  );
};
